import { useMemo } from "react";

import { colorsPalettes, colorsSets } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { TOP_RANK } from "app/common/benchmark/constants";
import type { TrafficShareMetricSchema } from "app/common/benchmark/types";

import { createOrderedData } from "./createOrderedData";

const colors = colorsSets.c.toArray();

type UseTableDataSchema = {
    primary: string;
    trafficShareData: TrafficShareMetricSchema;
    sorted?: boolean;
};

export const useTrafficShareTableData = ({ primary, trafficShareData, sorted }: UseTableDataSchema) => {
    const competitorsData = trafficShareData?.competitorsData;
    const primaryData = trafficShareData?.primaryData;
    const withOthers = !!trafficShareData?.othersData;
    const isPrimaryInTop = trafficShareData?.primaryData?.rank <= TOP_RANK;

    const primaryTableData = {
        key: primary,
        domain: primary,
        trafficShare: primaryData?.share,
        pop: primaryData?.periodOverPeriodShareDifference,
        yoy: primaryData?.yearOverYearShareDifference,
        color: colors[0],
        isPrimary: true,
        rank: primaryData?.rank,
    };

    const competitorsTableData = useMemo(
        () =>
            competitorsData &&
            Object.keys(competitorsData).map((competitor, index) => {
                return {
                    key: competitor,
                    domain: competitor,
                    trafficShare: competitorsData[competitor]?.share,
                    pop: competitorsData[competitor]?.periodOverPeriodShareDifference,
                    yoy: competitorsData[competitor]?.yearOverYearShareDifference,
                    color: colors[index + 1],
                    isPrimary: false,
                    rank: competitorsData[competitor].rank,
                };
            }),
        [competitorsData],
    );

    const otherData = withOthers
        ? {
              key: translate("bt.metrics.share.cell.others"),
              domain: translate("bt.metrics.share.cell.others"),
              trafficShare: trafficShareData?.othersData.share,
              mom: trafficShareData?.othersData.periodOverPeriodShareRate,
              color: colorsPalettes.carbon[100],
              isPrimary: false,
              rank: TOP_RANK,
              isOtherCompetitors: true,
          }
        : undefined;

    if (!trafficShareData || trafficShareData?.isLoading) {
        return [];
    }

    return createOrderedData({
        primaryData: primaryTableData,
        competitorsData: competitorsTableData,
        withOthers,
        otherData,
        isPrimaryInTop,
        sorted,
    });
};
